import React, { useEffect, useState } from 'react';
import { ref, onValue, update, off, remove } from 'firebase/database';
import { db } from '../firebase';

const MultiQuizScreen = ({ roomInfo, onFinish }) => {
  const { code, myId, isHost } = roomInfo;
  const [roomData, setRoomData] = useState(null);
  const [openedBlanks, setOpenedBlanks] = useState({});

  useEffect(() => {
    const roomRef = ref(db, `multiRooms/${code}`);
    const unsub = onValue(roomRef, snap => {
      const data = snap.val();
      if (!data) { onFinish(); return; }
      setRoomData(data);
      if (data.status === 'finished') onFinish();
    });
    return () => off(roomRef, 'value', unsub);
  }, [code]);

  useEffect(() => {
    setOpenedBlanks({});
  }, [roomData?.currentIndex]);

  const handleNext = async () => {
    if (!isHost || !roomData) return;
    const nextIndex = roomData.currentIndex + 1;
    if (nextIndex >= roomData.questions.length) {
      await update(ref(db, `multiRooms/${code}`), { status: 'finished' });
    } else {
      await update(ref(db, `multiRooms/${code}`), { currentIndex: nextIndex });
    }
  };

  const handleFinish = async () => {
    await remove(ref(db, `multiRooms/${code}`));
  };

  if (!roomData || !roomData.questions || roomData.questions.length === 0) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>読み込み中...</div>;
  }

  const currentQ = roomData.questions[roomData.currentIndex];
  const total = roomData.questions.length;
  const current = roomData.currentIndex;
  const players = Object.values(roomData.players || {});
  const hostPlayer = players.find(p => p.isHost);

  // ★ ソロと同じrequiredCount計算
  const requiredCount = currentQ.type === 'indent'
    ? (currentQ.rows || []).reduce((acc, row) =>
        acc + (row.text.match(/【[^】]+】/g) || []).length, 0)
    : currentQ.type === 'grouped'
      ? (currentQ.categories || []).reduce((acc, cat) =>
          acc + (cat.groups || []).reduce((a, g) =>
            a + (g.rows || []).reduce((b, row) =>
              b + (row.term?.match(/【[^】]+】/g) || []).length
                + (row.desc?.match(/【[^】]+】/g) || []).length
            , 0)
          , 0)
        , 0)
      : currentQ.type === 'list'
        ? (currentQ.items || []).reduce((acc, item) =>
            acc + 1 + (item.desc.match(/【[^】]+】/g) || []).length
          , 0)
        : (currentQ.q.match(/【[^】]+】/g) || []).length;

  const allOpened = requiredCount === 0 || Object.keys(openedBlanks).length >= requiredCount;

  // ★ ソロと同じrenderQuestionWithBlank（全type対応）
  const renderQuestionWithBlank = () => {
    // indent型
    if (currentQ.type === 'indent') {
      const renderBlanks = (text, prefix) => {
        const parts = text.split(/【([^】]+)】/);
        return parts.map((part, j) => {
          if (j % 2 === 0) return <span key={j}>{part}</span>;
          const bKey = `${prefix}_${j}`;
          const isOpened = !!openedBlanks[bKey];
          return (
            <span key={j}
              onClick={() => setOpenedBlanks(prev => ({ ...prev, [bKey]: true }))}
              style={{
                display: 'inline-block', padding: '0px 4px', borderRadius: '5px',
                border: `1.5px solid ${isOpened ? '#E74C3C' : '#3498db'}`,
                background: isOpened ? '#fdf2f2' : '#ebf5fb',
                color: isOpened ? '#E74C3C' : 'transparent',
                cursor: 'pointer', fontWeight: '500', fontSize: '1rem',
                userSelect: 'none', minWidth: `${part.length * 1.1}em`,
                textAlign: 'center', margin: '0 2px', verticalAlign: 'middle',
              }}>
              {isOpened ? part : '　'.repeat(part.length)}
            </span>
          );
        });
      };
      return (
        <div style={{ fontSize: '1rem', lineHeight: '2.2' }}>
          <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '4px' }}>{currentQ.q}</div>
          {currentQ.rows.map((row, rIndex) => (
            <div key={rIndex} style={{
              paddingLeft: row.level === 0 ? '0' : '2em',
              fontWeight: 'normal', color: row.level === 0 ? '#2c3e50' : '#444',
            }}>
              {renderBlanks(row.text, `ind_${rIndex}`)}
            </div>
          ))}
        </div>
      );
    }

    // grouped型
    if (currentQ.type === 'grouped') {
      const renderBlanks = (text, prefix) => {
        if (!text) return null;
        const parts = text.split(/【([^】]+)】/);
        return parts.map((part, j) => {
          if (j % 2 === 0) return <span key={j}>{part}</span>;
          const bKey = `${prefix}_${j}`;
          const isOpened = !!openedBlanks[bKey];
          return (
            <span key={j}
              onClick={() => setOpenedBlanks(prev => ({ ...prev, [bKey]: true }))}
              style={{
                display: 'inline-block', padding: '0px 6px', borderRadius: '5px',
                border: `1.5px solid ${isOpened ? '#E74C3C' : '#3498db'}`,
                background: isOpened ? '#fdf2f2' : '#ebf5fb',
                color: isOpened ? '#E74C3C' : 'transparent',
                cursor: 'pointer', fontWeight: 'bold', fontSize: '0.88rem',
                userSelect: 'none', minWidth: `${part.length * 1.1}em`,
                textAlign: 'center', margin: '0 2px', verticalAlign: 'middle',
              }}>
              {isOpened ? part : '　'.repeat(part.length)}
            </span>
          );
        });
      };
      return (
        <div style={{ fontSize: '0.88rem' }}>
          {currentQ.categories.map((cat, catIndex) => (
            <div key={catIndex} style={{
              marginBottom: '14px', borderRadius: '12px',
              border: '0.5px solid #e1e8ed', overflow: 'hidden', background: 'white',
            }}>
              <div style={{
                padding: '7px 14px', background: '#f8f9fa',
                fontWeight: '500', fontSize: '0.85rem', borderBottom: '0.5px solid #e1e8ed',
              }}>
                {cat.label}
              </div>
              {cat.groups.map((group, gIndex) => (
                <div key={gIndex} style={{
                  borderBottom: gIndex < cat.groups.length - 1 ? '0.5px solid #e1e8ed' : 'none',
                }}>
                  {group.label && (
                    <div style={{ padding: '5px 14px 2px', fontSize: '0.75rem', color: '#95a5a6', fontWeight: '500' }}>
                      {group.label}
                    </div>
                  )}
                  {group.rows.map((row, rIndex) => (
                    <div key={rIndex} style={{
                      display: 'flex', alignItems: 'baseline',
                      padding: '3px 14px 6px', gap: '6px', flexWrap: 'wrap',
                    }}>
                      {row.term && (
                        <>
                          <span style={{ color: '#2c3e50', flexShrink: 0 }}>
                            {renderBlanks(row.term, `g_${catIndex}_${gIndex}_${rIndex}_term`)}
                          </span>
                          <span style={{ color: '#bdc3c7', fontSize: '0.8rem' }}>：</span>
                        </>
                      )}
                      <span style={{ color: '#555', lineHeight: '1.7', flex: 1 }}>
                        {renderBlanks(row.desc, `g_${catIndex}_${gIndex}_${rIndex}_desc`)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }

    // list型
    if (currentQ.type === 'list') {
      return (
        <>
          <div style={{ fontWeight: 'bold', color: '#2c3e50', marginBottom: '8px' }}>{currentQ.q}</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {currentQ.items.map((item, itemIndex) => {
              const descParts = item.desc.split(/【([^】]+)】/);
              return (
                <li key={itemIndex} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '10px' }}>
                  <span style={{ fontWeight: 'bold', minWidth: '1.5em' }}>{item.label}</span>
                  <span
                    onClick={() => setOpenedBlanks(prev => ({ ...prev, [`list_${itemIndex}_term`]: true }))}
                    style={{
                      display: 'inline-block', padding: '1px 8px', borderRadius: '6px',
                      border: `2px solid ${openedBlanks[`list_${itemIndex}_term`] ? '#E74C3C' : '#3498db'}`,
                      background: openedBlanks[`list_${itemIndex}_term`] ? '#fdf2f2' : '#ebf5fb',
                      color: openedBlanks[`list_${itemIndex}_term`] ? '#E74C3C' : 'transparent',
                      cursor: 'pointer', fontWeight: 'bold', fontSize: '1.05rem',
                      userSelect: 'none', minWidth: `${item.term.length * 1.2}em`,
                      height: '28px', lineHeight: '28px', textAlign: 'center',
                    }}
                  >
                    {openedBlanks[`list_${itemIndex}_term`] ? item.term : '　'.repeat(item.term.length)}
                  </span>
                  <span style={{ fontWeight: 'bold', margin: '0 4px' }}>：</span>
                  <span style={{ fontSize: '1rem', lineHeight: '1.8' }}>
                    {descParts.map((part, j) => {
                      if (j % 2 === 0) return <span key={j}>{part}</span>;
                      const bKey = `list_${itemIndex}_desc_${j}`;
                      const isOpened = !!openedBlanks[bKey];
                      return (
                        <span key={j}
                          onClick={() => setOpenedBlanks(prev => ({ ...prev, [bKey]: true }))}
                          style={{
                            display: 'inline-block', padding: '1px 4px', borderRadius: '6px',
                            border: `2px solid ${isOpened ? '#E74C3C' : '#3498db'}`,
                            background: isOpened ? '#fdf2f2' : '#ebf5fb',
                            color: isOpened ? '#E74C3C' : 'transparent',
                            cursor: 'pointer', fontWeight: 'bold', fontSize: '1.05rem',
                            userSelect: 'none', minWidth: `${part.length * 1.1}em`,
                            textAlign: 'center', margin: '0 2px', verticalAlign: 'middle',
                          }}
                        >
                          {isOpened ? part : '　'.repeat(part.length)}
                        </span>
                      );
                    })}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      );
    }

    // 通常型
    const parts = currentQ.q.split(/【([^】]+)】/);
    let blankIndex = 0;
    return (
      <>
        {parts.map((part, i) => {
          if (i % 2 === 0) return <span key={i}>{part}</span>;
          const thisIndex = blankIndex++;
          const isOpened = !!openedBlanks[thisIndex];
          const charCount = part.length;
          return (
            <span key={i}
              onClick={() => setOpenedBlanks(prev => ({ ...prev, [thisIndex]: true }))}
              style={{
                display: 'inline-block', minWidth: `${charCount * 1.1}em`,
                height: '28px', lineHeight: '28px', padding: '1px 4px',
                borderRadius: '6px', border: `2px solid ${isOpened ? '#E74C3C' : '#3498db'}`,
                background: isOpened ? '#fdf2f2' : '#ebf5fb',
                color: isOpened ? '#E74C3C' : 'transparent',
                cursor: 'pointer', textAlign: 'center', margin: '0 3px',
                userSelect: 'none', fontSize: '1.1rem', fontWeight: 'bold', verticalAlign: 'middle',
              }}
            >
              {isOpened ? part : '　'.repeat(charCount)}
            </span>
          );
        })}
      </>
    );
  };

  return (
    <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

      {/* ヘッダー */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {players.map((p, i) => (
            <div key={i} style={{
              background: p.isHost ? '#ebf5fb' : '#f8f9fa',
              border: p.isHost ? '1px solid #3498db' : '1px solid #e1e8ed',
              borderRadius: '16px', padding: '4px 10px',
              fontSize: '0.75rem', fontWeight: 'bold',
              color: p.isHost ? '#3498db' : '#7f8c8d'
            }}>
              {p.isHost ? '👑' : '👤'} {p.name}
            </div>
          ))}
        </div>
        <span style={{ fontWeight: 'bold', color: '#7f8c8d', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
          {current + 1} / {total}
        </span>
      </div>

      {/* ルームコード */}
      <div style={{ textAlign: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '0.7rem', color: '#bdc3c7', letterSpacing: '0.1em' }}>ROOM: {code}</span>
      </div>

      {/* 問題カード */}
      <div style={{
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
        borderRadius: '24px', border: '1px solid rgba(255,255,255,0.3)',
        boxShadow: '0 20px 50px rgba(149,157,165,0.1)',
        padding: '20px', width: '100%', boxSizing: 'border-box',
        textAlign: 'left', marginBottom: '16px',
      }}>
        <div style={{ fontSize: '0.8rem', color: '#3498db', marginBottom: '8px' }}>
          {currentQ.ch} - {currentQ.s}
        </div>
        <div style={{ fontSize: '1.1rem', lineHeight: '2.5', margin: '12px 0' }}>
          {renderQuestionWithBlank()}
        </div>

        {allOpened && currentQ.n && (
          <div style={{
            background: '#f8f9fa', padding: '15px', borderRadius: '10px',
            marginTop: '16px', borderLeft: '5px solid #2ecc71', fontSize: '0.9rem'
          }}>
            <strong>解説:</strong> {currentQ.n}
          </div>
        )}
      </div>

      {/* ボタン */}
      {isHost ? (
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handleFinish}
            style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '2px solid #e74c3c', background: 'white', cursor: 'pointer', color: '#e74c3c', fontWeight: 'bold' }}>
            終了
          </button>
          <button onClick={handleNext}
            style={{ flex: 3, padding: '12px', background: '#2ecc71', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem' }}>
            {current + 1 === total ? '✅ 完了' : '次へ →'}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <p style={{ color: '#95a5a6', fontSize: '0.85rem', margin: 0 }}>
            👑 {hostPlayer?.name || 'ホスト'}が次の問題に進めます
          </p>
          <button onClick={onFinish}
            style={{ padding: '8px 20px', borderRadius: '20px', border: '2px solid #e1e8ed', background: 'white', cursor: 'pointer', color: '#7f8c8d', fontWeight: 'bold', fontSize: '0.85rem' }}>
            退出する
          </button>
        </div>
      )}
    </div>
  );
};

export default MultiQuizScreen;